import React, { useState, useMemo, Fragment, useEffect } from 'react';
import { Edit, Trash2, PieChart as PieIcon, Calendar, Filter, Award, TrendingUp, Wallet, Layers, DollarSign, BookOpen, LineChart as LineIcon, BarChart3, Target, Activity, ShieldCheck, AlertTriangle, Clock, X, List, RefreshCcw, Hourglass, Eye , Info, CheckCircle} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip as RechartsTooltip, Legend, LineChart, Line, AreaChart, Area, LabelList, ComposedChart, PieChart, Pie, Cell } from 'recharts';
import Chart from 'react-apexcharts';
import { useStockModal } from '../context/StockModalContext';
import { useTranslation } from 'react-i18next';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { api } from '../services/apiClient';

const LYNCH_DATA = {
    "Fast Grower": { icon: "🚀", color: "text-purple-700 bg-purple-100 hover:bg-purple-200 border-purple-200", desc: "Entreprise qui croît de ~20% par an et réinvestit massivement.", sell: "Vendre lorsque la croissance des ventes ralentit." },
    "Slow Grower": { icon: "🐢", color: "text-amber-700 bg-amber-100 hover:bg-amber-200 border-amber-200", desc: "Entreprise mature et stable.", sell: "Vendre si l'entreprise coupe son dividende." },
    "Stalwart": { icon: "🏛️", color: "text-blue-700 bg-blue-100 hover:bg-blue-200 border-blue-200", desc: "Pilier ultra-solide, marque mondiale.", sell: "Vendre si elle devient significativement surévaluée." },
    "Cyclical": { icon: "🔄", color: "text-orange-700 bg-orange-100 hover:bg-orange-200 border-orange-200", desc: "Performances liées à la macro-économie.", sell: "Vendre quand TOUT va bien !" },
    "Turnaround": { icon: "🚑", color: "text-rose-700 bg-rose-100 hover:bg-rose-200 border-rose-200", desc: "En grande difficulté mais avec un plan de redressement.", sell: "Vendre dès que le marché réalise que le redressement a fonctionné." },
    "Asset Play": { icon: "💎", color: "text-emerald-700 bg-emerald-100 hover:bg-emerald-200 border-emerald-200", desc: "Entreprise possédant des actifs cachés/sous-évalués.", sell: "Vendre quand le marché prend conscience de la valeur des actifs." },
    "Inconnu": { icon: "⏳", color: "text-slate-500 bg-slate-100 hover:bg-slate-200 border-slate-200", desc: "L'algorithme analyse actuellement cette action.", sell: "En attente des données." }
};

const LynchModal = ({ category, onClose }) => {
    if (!category) return null;
    const info = LYNCH_DATA[category] || LYNCH_DATA["Inconnu"];
    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[200] p-4 backdrop-blur-sm" onClick={onClose}>
            <div className="bg-white rounded-2xl p-6 w-full max-w-sm animate-in zoom-in duration-200 shadow-2xl" onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-start mb-4">
                    <div>
                        <div className="text-[10px] uppercase font-black tracking-widest text-slate-400 mb-1">Méthode Peter Lynch</div>
                        <h3 className="font-black text-xl flex items-center gap-2 text-slate-800"><span>{info.icon}</span> {category}</h3>
                    </div>
                    <button onClick={onClose} className="p-1 rounded-full hover:bg-slate-100"><X className="text-slate-400 hover:text-slate-600"/></button>
                </div>
                <p className="text-slate-600 text-sm mb-5 leading-relaxed">{info.desc}</p>
                <div className="bg-rose-50 border border-rose-100 p-4 rounded-xl relative overflow-hidden">
                    <div className="absolute -right-2 -top-2 text-rose-100 opacity-50"><AlertTriangle size={64}/></div>
                    <div className="relative z-10"><h4 className="font-black text-rose-800 text-xs mb-2 uppercase flex items-center gap-1"><Target size={14}/> Quand Vendre ?</h4><p className="text-sm text-rose-700 font-medium leading-relaxed">{info.sell}</p></div>
                </div>
            </div>
        </div>
    );
};

export const formatMoney = (val, curr = 'USD') => { 
    if (val === undefined || val === null || isNaN(val)) return "--"; 
    try {
        const safeCurr = (curr && curr.length === 3) ? curr.toUpperCase() : 'USD';
        return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: safeCurr }).format(val); 
    } catch(e) {
        return `${val.toFixed(2)} $`;
    }
};

export const formatQty = (val) => { if (val === undefined || val === null) return "-"; return val % 1 === 0 ? val : val.toFixed(4); };

export const PortfolioStatsGrid = ({ summary }) => {
    const { t } = useTranslation();
    if (!summary) return null;
    const { total_value, currency, total_pl, advanced_stats } = summary;
    const stats = advanced_stats || { ytd:0, "1y":0, "3y":0, "5y":0, all:0, avg_holding_days:0, turnover:0, mwr:0 };

    return (
        <div className="flex flex-col xl:flex-row gap-6 mb-8 animate-in fade-in duration-500">
            <div className="flex-[1.5] bg-gradient-to-br from-indigo-600 via-indigo-700 to-violet-800 text-white p-8 rounded-3xl shadow-xl relative overflow-hidden flex flex-col justify-center min-h-[220px]">
                <div className="absolute top-0 right-0 p-6 opacity-10 transform translate-x-4 -translate-y-4">
                    <Wallet size={160}/>
                </div>
                <div className="relative z-10">
                    <span className="text-indigo-200 text-sm font-bold uppercase tracking-widest mb-3 block">{t('portfolio.stats_total_val', 'Valeur Totale')}</span>
                    <div className="text-5xl font-black tracking-tight mb-5">{formatMoney(total_value, currency)}</div>
                    <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-md px-4 py-2 rounded-xl border border-white/10">
                        <TrendingUp size={18} className={total_pl >= 0 ? 'text-emerald-300' : 'text-rose-300'}/>
                        <span className="text-sm font-bold text-white">
                            {total_pl >= 0 ? '+' : ''}{formatMoney(total_pl, currency)} ({t('portfolio.stats_latent', 'Latent')})
                        </span>
                    </div>
                </div>
            </div>

            <div className="flex-1 bg-slate-900 text-white p-8 rounded-3xl shadow-xl relative overflow-hidden flex flex-col justify-center border border-slate-800 min-h-[220px]">
                <div className="absolute -right-4 -bottom-4 text-emerald-500/10"><Target size={140}/></div>
                <div className="relative z-10">
                    <div className="flex items-center gap-2 mb-3">
                        <span className="px-3 py-1 bg-emerald-500/20 text-emerald-400 text-[10px] font-black uppercase tracking-wider rounded-full border border-emerald-500/30">Précis</span>
                    </div>
                    <span className="text-slate-400 text-xs font-bold uppercase tracking-widest mb-1 block">Performance MWR</span>
                    <div className="text-4xl font-black tracking-tight text-emerald-400 mb-2">
                        {stats.mwr > 0 ? '+' : ''}{typeof stats.mwr === 'number' ? stats.mwr.toFixed(2) : '0.00'}%
                    </div>
                    <p className="text-[11px] text-slate-500 font-medium leading-relaxed max-w-[90%]">
                        Taux de rentabilité interne (XIRR). Reflète votre performance réelle en intégrant le timing de vos dépôts et retraits.
                    </p>
                </div>
            </div>

            <div className="flex-[1.5] grid grid-cols-2 gap-4">
                <div className="bg-white p-5 rounded-3xl border border-slate-100 shadow-sm flex flex-col justify-center hover:shadow-md transition-shadow">
                    <span className="text-[10px] font-bold uppercase text-slate-400 tracking-wider mb-1 flex items-center gap-1"><Award size={12}/> Rend. Global (TWR)</span>
                    <div className={`text-2xl font-black ${stats.all >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                        {stats.all >= 0 ? '+' : ''}{typeof stats.all === 'number' ? stats.all.toFixed(2) : '0.00'}%
                    </div>
                </div>
                <div className="bg-white p-5 rounded-3xl border border-slate-100 shadow-sm flex flex-col justify-center hover:shadow-md transition-shadow">
                    <span className="text-[10px] font-bold uppercase text-slate-400 tracking-wider mb-1 flex items-center gap-1"><TrendingUp size={12}/> Année en cours (YTD)</span>
                    <div className={`text-2xl font-black ${stats.ytd >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                        {stats.ytd >= 0 ? '+' : ''}{typeof stats.ytd === 'number' ? stats.ytd.toFixed(2) : '0.00'}%
                    </div>
                </div>
                <div className="bg-white p-5 rounded-3xl border border-slate-100 shadow-sm flex flex-col justify-center hover:shadow-md transition-shadow">
                    <span className="text-[10px] font-bold uppercase text-slate-400 tracking-wider mb-1 flex items-center gap-1"><Hourglass size={12}/> Détention Moyenne</span>
                    <div className="text-2xl font-black text-slate-800">
                        {(stats.avg_holding_days / 365).toFixed(1)} <span className="text-sm text-slate-400">ans</span>
                    </div>
                </div>
                <div className="bg-white p-5 rounded-3xl border border-slate-100 shadow-sm flex flex-col justify-center hover:shadow-md transition-shadow">
                    <span className="text-[10px] font-bold uppercase text-slate-400 tracking-wider mb-1 flex items-center gap-1"><RefreshCcw size={12}/> Turnover (1 An)</span>
                    <div className="text-2xl font-black text-slate-800">
                        {typeof stats.turnover === 'number' ? stats.turnover.toFixed(1) : '0.0'}%
                    </div>
                </div>
            </div>
        </div>
    );
};

export const generatePDF = (summary, analytics) => {
    if (!summary || !summary.positions) return alert("Données insuffisantes pour le PDF.");
    try {
        const doc = new jsPDF();
        const dateStr = new Date().toLocaleDateString();
        doc.setFontSize(22); doc.setTextColor(40, 40, 40); doc.text("Rapport de Portefeuille", 14, 20);
        doc.setFontSize(10); doc.setTextColor(100, 100, 100); doc.text(`Généré le ${dateStr}`, 14, 28);
        doc.setFillColor(245, 247, 250); doc.roundedRect(14, 35, 180, 30, 3, 3, 'F');
        doc.setFontSize(12); doc.setTextColor(60, 60, 60); doc.text(`Valeur Totale :`, 20, 48);
        doc.setFontSize(14); doc.setTextColor(0, 0, 0); doc.text(formatMoney(summary.total_value || 0, summary.currency), 20, 56);
        doc.setFontSize(12); doc.setTextColor(60, 60, 60); doc.text(`Plus-Value Latente :`, 80, 48);
        doc.setFontSize(14); const pl = summary.total_pl || 0; doc.setTextColor(pl >= 0 ? 0 : 200, pl >= 0 ? 150 : 0, 0); doc.text(formatMoney(pl, summary.currency), 80, 56);
        if(analytics?.metrics) { doc.setFontSize(10); doc.setTextColor(80, 80, 80); const m = analytics.metrics; doc.text(`Beta: ${m.beta || 0}  |  Sharpe: ${m.sharpe || 0}  |  Max Drawdown: ${m.max_drawdown || 0}%`, 20, 80); }
        const tableColumn = ["Symbole", "Catégorie", "Qté", "PRU", "Prix", "Valeur", "Gain %"];
        const tableRows = summary.positions.map(p => { const truePru = p.pru || 0; const truePrice = p.price || 0; const trueQty = p.qty || 0; const unrealizedPnlPercent = truePru > 0 ? ((truePrice / truePru) - 1) * 100 : 0; return [ p.symbol || "?", p.lynch_category || "Inconnu", formatQty(trueQty), formatMoney(truePru, p.currency), formatMoney(truePrice, p.currency), formatMoney(p.value_converted || p.value || 0, 'USD'), `${unrealizedPnlPercent.toFixed(2)}%` ]; });
        autoTable(doc, { head: [tableColumn], body: tableRows, startY: 90, theme: 'grid', headStyles: { fillColor: [79, 70, 229], textColor: [255, 255, 255] }, styles: { fontSize: 9, cellPadding: 3 }, });
        doc.save(`Rapport_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (e) { console.error("Erreur PDF:", e); alert("Erreur technique PDF."); }
};

const BaggerBadge = ({ value }) => {
    if (!value || value < 2) return null;
    let style = value >= 10 ? "bg-yellow-100 text-yellow-800 border-yellow-300" : value >= 5 ? "bg-purple-100 text-purple-800 border-purple-300" : "bg-blue-100 text-blue-800 border-blue-300";
    return <span className={`ml-2 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded border text-[9px] font-black uppercase tracking-wide ${style}`} title={`${value.toFixed(2)} Bagger`}><Award size={10} /> {value.toFixed(1)}x</span>;
};

export const PositionsTable = ({ positions, sortConfig, onSort, onEdit, onDelete, selectedSymbol, onSelectSymbol, onOpenOps }) => {
    const { t } = useTranslation();
    const { openStockModal } = useStockModal();
    const [selectedLynch, setSelectedLynch] = useState(null); 
    const [isGrouped, setIsGrouped] = useState(true); 
    
    if (!positions || positions.length === 0) return <div className="p-8 text-center text-slate-400">{t('portfolio.no_active')}</div>;
    const Th = ({ k, label, align = "right" }) => (<th className={`px-4 py-3 cursor-pointer hover:text-indigo-600 text-${align} text-xs uppercase text-slate-500 font-bold`} onClick={() => onSort(k)}>{label}</th>);
    
    const renderRow = (p) => {
        const lynchCat = p.lynch_category || "Inconnu";
        const lynchStyle = LYNCH_DATA[lynchCat]?.color || LYNCH_DATA["Inconnu"].color;
        const lynchIcon = LYNCH_DATA[lynchCat]?.icon || "❓";
        const rsi = p.rsi;
        const truePru = p.pru || 0; const truePrice = p.price || 0; const trueQty = p.qty || 0;
        const unrealizedPnlRaw = (truePrice - truePru) * trueQty;
        const unrealizedPnlPercent = truePru > 0 ? ((truePrice / truePru) - 1) * 100 : 0;
        const isPositive = unrealizedPnlRaw >= 0;
        const isSelected = selectedSymbol === p.symbol;

        return (
            <tr 
                key={p.symbol} 
                onClick={() => onSelectSymbol(p.symbol)}
                className={`transition-colors cursor-pointer border-b border-slate-100 last:border-b-0 group ${isSelected ? 'bg-indigo-50 border-l-4 border-l-indigo-500' : 'hover:bg-slate-50'}`}
            >
                <td className="px-4 py-3">
                    <div className={`font-bold ${isSelected ? 'text-indigo-900' : 'text-slate-800'} cursor-pointer hover:text-indigo-600 flex items-center gap-2`} onClick={(e) => { e.stopPropagation(); openStockModal(p.symbol); }}>{p.name} <BaggerBadge value={p.bagger} /></div>
                    <div className="flex items-center gap-2 mt-1.5">
                        <span className={`text-xs font-mono ${isSelected ? 'text-indigo-500' : 'text-slate-400'}`}>{p.symbol}</span>
                        {!isGrouped && (<button onClick={(e) => { e.stopPropagation(); setSelectedLynch(lynchCat); }} className={`text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded border transition-colors flex items-center gap-1 ${lynchStyle}`} title="Voir la strategy"><span>{lynchIcon}</span> {lynchCat}</button>)}
                    </div>
                </td>
                <td className="px-4 py-3 text-right font-mono text-slate-600">{formatQty(trueQty)}</td>
                <td className="px-4 py-3 text-right font-mono text-slate-500">{formatMoney(truePru, p.currency)}</td>
                <td className="px-4 py-3 text-right"><div className={`font-mono font-bold ${isSelected ? 'text-indigo-700' : 'text-slate-700'}`}>{formatMoney(truePrice, p.currency)}</div></td>
                <td className="px-4 py-3 text-right font-bold text-slate-900">{formatMoney(p.value || p.value_converted, 'USD')}</td>
                <td className="px-4 py-3 text-right"><div className={`font-bold ${isPositive ? 'text-emerald-600' : 'text-rose-600'}`}>{isPositive ? '+' : ''}{formatMoney(unrealizedPnlRaw, p.currency)}</div><div className={`text-xs ${isPositive ? 'text-emerald-500' : 'text-rose-500'}`}>{isPositive ? '+' : ''}{unrealizedPnlPercent.toFixed(2)}%</div></td>
                <td className="px-4 py-3 text-right text-slate-500 text-xs">{p.weight?.toFixed(1)}%</td>
                <td className="px-4 py-3 text-center">
                    {rsi !== undefined && rsi !== null ? (
                        <span className={`inline-block px-2 py-1 rounded text-xs font-bold ${rsi < 30 ? "bg-emerald-100 text-emerald-700" : rsi > 70 ? "bg-rose-100 text-rose-700" : "bg-slate-100 text-slate-600"}`} title={rsi < 30 ? "Survente (Opportunité d achat)" : rsi > 70 ? "Surachat (Prudence)" : "Zone Neutre"}>
                            {rsi.toFixed(0)}
                        </span>
                    ) : <span className="text-xs text-slate-300">-</span>}
                </td>
                <td className="px-4 py-3 text-center flex justify-center gap-3">
                    <button onClick={(e) => { e.stopPropagation(); onSelectSymbol(p.symbol); }} className={`transition-colors ${isSelected ? 'text-indigo-600' : 'text-slate-300 hover:text-indigo-600'}`} title="Ouvrir l'analyse graphique"><Eye size={16}/></button>
                    <button onClick={(e) => { e.stopPropagation(); onOpenOps(p.symbol); }} className="text-slate-300 hover:text-indigo-600 transition-colors" title="Voir les opérations"><List size={16}/></button>
                    <button onClick={(e) => { e.stopPropagation(); onEdit(p); }} className="text-slate-300 hover:text-indigo-600 transition-colors"><Edit size={14}/></button>
                    <button onClick={(e) => { e.stopPropagation(); onDelete(p.symbol); }} className="text-slate-300 hover:text-red-600 transition-colors"><Trash2 size={14}/></button>
                </td>
            </tr>
        );
    };

    const renderTableBody = () => {
        if (!isGrouped) return positions.map(p => renderRow(p));
        const grouped = positions.reduce((acc, p) => { const cat = p.lynch_category || "Inconnu"; if (!acc[cat]) acc[cat] = []; acc[cat].push(p); return acc; }, {});
        return Object.keys(grouped).sort().map(cat => {
            const info = LYNCH_DATA[cat] || LYNCH_DATA["Inconnu"];
            return (
                <Fragment key={cat}>
                    <tr className="bg-slate-100/50 border-y border-slate-200">
                        <td colSpan="9" className="px-4 py-2">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2"><span className={`text-xs font-black uppercase tracking-wider px-2 py-1 rounded border ${info.color}`}>{info.icon} {cat}</span><span className="text-xs font-bold text-slate-400">({grouped[cat].length} {t('portfolio.assets_count')})</span></div>
                                <button onClick={() => setSelectedLynch(cat)} className="text-xs text-indigo-600 hover:text-indigo-800 font-bold flex items-center gap-1"><BookOpen size={12}/> {t('portfolio.sell_strategy')}</button>
                            </div>
                        </td>
                    </tr>
                    {grouped[cat].map(p => renderRow(p))}
                </Fragment>
            );
        });
    };

    return (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <LynchModal category={selectedLynch} onClose={() => setSelectedLynch(null)} />
            <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                <h3 className="font-bold text-slate-700 flex items-center gap-2"><List size={16} className="text-indigo-500"/> {t('portfolio.assets_title')}</h3>
                <button onClick={() => setIsGrouped(!isGrouped)} className={`text-xs font-bold px-3 py-1.5 rounded-lg flex items-center gap-2 transition-all shadow-sm ${isGrouped ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'}`}>
                    <Layers size={14} /> {isGrouped ? t('portfolio.ungroup') : t('portfolio.group_category')}
                </button>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                    <thead className="bg-white border-b border-slate-200">
                        <tr>
                            <Th k="name" label={t('portfolio.col_title')} align="left"/>
                            <Th k="qty" label={t('portfolio.col_qty')}/>
                            <Th k="pru" label={t('portfolio.col_pru')}/>
                            <Th k="price" label={t('portfolio.col_price')}/>
                            <Th k="value_converted" label={t('portfolio.col_val')}/>
                            <Th k="gain" label={t('portfolio.col_pnl')}/>
                            <Th k="weight" label={t('portfolio.col_weight')}/>
                            <Th k="rsi" label="RSI (14j)"/>
                            <th className="px-4 py-3 text-center text-xs uppercase text-slate-500 font-bold">{t('portfolio.col_actions')}</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">{renderTableBody()}</tbody>
                </table>
            </div>
        </div>
    );
};

export const PositionDetailPanel = ({ portfolioId, symbol, positionData, onClose }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedPeriod, setSelectedPeriod] = useState('1y');

    useEffect(() => {
        if (!portfolioId || !symbol) return;
        setLoading(true);
        api.get(`/portfolio/${portfolioId}/position-chart/${symbol}?period=${selectedPeriod}`)
            .then(res => setData(res.data))
            .catch(err => console.error(err))
            .finally(() => setLoading(false));
    }, [portfolioId, symbol, selectedPeriod]);

    if (loading) {
        return (
            <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center z-[200] p-4 backdrop-blur-sm">
                <div className="bg-white p-8 rounded-2xl shadow-2xl flex flex-col items-center justify-center w-80 h-64 animate-in zoom-in duration-200">
                    <RefreshCcw size={48} className="animate-spin text-indigo-500 mb-6" />
                    <p className="text-slate-600 font-bold text-center">Calcul du graphe {selectedPeriod} pour {symbol}...</p>
                </div>
            </div>
        );
    }

    if (!data || !data.chart || data.chart.length === 0) {
        return (
            <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center z-[200] p-4 backdrop-blur-sm" onClick={onClose}>
                <div className="bg-white p-8 rounded-2xl shadow-2xl flex flex-col items-center justify-center w-80 animate-in zoom-in duration-200" onClick={e => e.stopPropagation()}>
                    <AlertTriangle size={48} className="text-rose-400 mb-6" />
                    <p className="text-slate-600 font-bold text-center">Aucune donnée boursière trouvée pour {symbol} ({selectedPeriod}).</p>
                    <div className="mt-6 flex flex-wrap gap-2 justify-center">
                        <button onClick={() => setSelectedPeriod('1y')} className="btn-secondary text-xs">Essayer 1 An</button>
                        <button onClick={onClose} className="btn-secondary text-xs">Fermer</button>
                    </div>
                </div>
            </div>
        );
    }

    const areaData = data.chart.map(pt => ({ x: pt.x, y: pt.y[3] }));

    const pointAnnotations = data.transactions
        .filter(t => t.type === 'BUY' || t.type === 'SELL')
        .map(t => {
            let color = t.type === 'BUY' ? '#10b981' : '#ef4444';
            let text = t.type === 'BUY' ? `B (${formatQty(t.qty)})` : `S (${formatQty(t.qty)})`;
            let offsetY = t.type === 'BUY' ? 10 : -10;

            return {
                x: t.x,
                y: t.y,
                marker: { size: 5, fillColor: color, strokeColor: '#fff', strokeWidth: 2, shape: 'circle' },
                label: {
                    borderColor: color,
                    offsetY: offsetY,
                    style: { color: '#fff', background: color, fontSize: '10px', fontWeight: 'bold', padding: { left: 4, right: 4, top: 2, bottom: 2 } },
                    text: text
                }
            };
        });

    const xaxisAnnotations = data.transactions
        .filter(t => t.type === 'DIVIDEND')
        .map(t => {
            return {
                x: t.x,
                strokeDashArray: 4,
                borderColor: '#a855f7',
                label: {
                    borderColor: '#a855f7',
                    position: 'bottom',
                    offsetY: -5,
                    style: { color: '#fff', background: '#a855f7', fontSize: '10px', fontWeight: 'bold', padding: { left: 4, right: 4, top: 2, bottom: 2 } },
                    text: `Div ${formatMoney(t.y, '')}`
                }
            };
        });

    const yaxisAnnotations = positionData.pru > 0 ? [{
        y: positionData.pru,
        borderColor: '#ef4444',
        strokeDashArray: 4,
        label: {
            borderColor: '#ef4444',
            style: { color: '#fff', background: '#ef4444', fontSize: '11px', fontWeight: 'bold', padding: { left: 6, right: 6, top: 3, bottom: 3 } },
            text: 'PRU: ' + formatMoney(positionData.pru, positionData.currency)
        }
    }] : [];

    const chartOptions = {
        chart: { type: 'area', fontFamily: 'system-ui, -apple-system, sans-serif', animations: { enabled: false }, toolbar: { show: false } },
        colors: ['#4f46e5'], 
        dataLabels: { enabled: false },
        stroke: { curve: 'straight', width: 1.5 },
        fill: { type: 'solid', opacity: 0.1 },
        xaxis: { type: 'datetime', labels: { style: { colors: '#64748b', fontWeight: 600 } }, tooltip: { enabled: false } },
        yaxis: { labels: { style: { colors: '#64748b', fontWeight: 600 }, formatter: (value) => { return value ? value.toFixed(2) : ''; } } },
        grid: { borderColor: '#f1f5f9', strokeDashArray: 4 },
        annotations: { points: pointAnnotations, xaxis: xaxisAnnotations, yaxis: yaxisAnnotations },
        tooltip: { x: { format: 'dd MMM yyyy' }, theme: 'light', y: { formatter: function(val) { return val ? val.toFixed(2) : ""; } } }
    };

    const series = [{ name: 'Prix', data: areaData }];

    const PeriodButton = ({ period, label }) => (
        <button 
            onClick={() => setSelectedPeriod(period)} 
            className={`px-3 py-1.5 rounded text-[11px] font-bold uppercase transition-all ${selectedPeriod === period ? 'bg-slate-200 text-slate-800 shadow-sm border border-slate-300' : 'text-slate-500 hover:text-slate-800 hover:bg-slate-100'}`}
        >
            {label}
        </button>
    );

    return (
        <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center z-[200] p-4 sm:p-8 backdrop-blur-sm" onClick={onClose}>
            <div className="bg-white rounded-2xl w-full max-w-[95vw] h-[90vh] flex flex-col animate-in zoom-in duration-300 shadow-2xl overflow-hidden border border-slate-100" onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center p-6 border-b border-slate-100 bg-white shrink-0">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-white border border-slate-200 rounded-2xl flex items-center justify-center text-indigo-600 shadow-sm">
                            <LineIcon size={24} />
                        </div>
                        <div>
                            <h3 className="font-black text-3xl text-slate-950">{symbol}</h3>
                            <p className="text-sm font-bold text-slate-500">Analyse Graphique Complète & Points d'Intervention</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="w-11 h-11 flex items-center justify-center rounded-full bg-white border border-slate-200 text-slate-400 hover:text-slate-900 hover:bg-slate-100 hover:border-slate-300 transition-all shadow-sm">
                        <X size={22} />
                    </button>
                </div>
                
                <div className="px-6 py-5 bg-white border-b border-slate-100 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 shrink-0">
                    <div><label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Quantité</label><div className="font-black text-xl text-slate-950 font-mono">{formatQty(positionData.qty)}</div></div>
                    <div><label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">PRU Moyen</label><div className="font-black text-xl text-slate-950 font-mono">{formatMoney(positionData.pru, positionData.currency)}</div></div>
                    <div><label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Valeur Actuelle</label><div className="font-black text-xl text-slate-950 font-mono">{formatMoney(positionData.value, positionData.currency)}</div></div>
                    
                    <div className={`p-3 rounded-xl border ${positionData.gain >= 0 ? 'bg-emerald-50 border-emerald-100' : 'bg-rose-50 border-rose-100'}`}>
                        <label className={`block text-[10px] font-bold uppercase tracking-wider mb-1 ${positionData.gain >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>PV Latente</label>
                        <div className={`font-black text-lg md:text-xl font-mono ${positionData.gain >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                            {positionData.gain >= 0 ? '+' : ''}{formatMoney(positionData.gain, positionData.currency)}
                        </div>
                    </div>
                    
                    <div className="p-3 rounded-xl border bg-purple-50 border-purple-100">
                        <label className="block text-[10px] font-bold uppercase tracking-wider mb-1 text-purple-700">Dividendes</label>
                        <div className="font-black text-lg md:text-xl font-mono text-purple-600">
                            +{formatMoney(positionData.collected_divs || 0, positionData.currency)}
                        </div>
                    </div>
                    
                    <div className={`p-3 rounded-xl shadow-inner ${positionData.total_gain >= 0 ? 'bg-emerald-500 text-white' : 'bg-rose-500 text-white'}`}>
                        <label className="block text-[10px] font-bold uppercase tracking-wider mb-1 text-white/80">Gain Total Réel</label>
                        <div className="font-black text-lg md:text-xl font-mono flex items-baseline gap-2">
                            <span>{positionData.total_gain >= 0 ? '+' : ''}{formatMoney(positionData.total_gain || positionData.gain, positionData.currency)}</span>
                            <span className="text-[11px] font-bold text-white/90">({(positionData.total_gain_p || positionData.gain_p).toFixed(2)}%)</span>
                        </div>
                    </div>
                </div>

                <div className="flex justify-between items-center p-4 bg-white border-b border-slate-100 shrink-0">
                    <div className="flex items-center gap-1.5 bg-slate-50 p-1 rounded-lg border border-slate-100 overflow-x-auto">
                        <PeriodButton period="1m" label="1M" /><PeriodButton period="3m" label="3M" /><PeriodButton period="6m" label="6M" /><PeriodButton period="1y" label="1A" /><PeriodButton period="2y" label="2A" /><PeriodButton period="3y" label="3A" /><PeriodButton period="5y" label="5A" /><PeriodButton period="all" label="ALL" />
                    </div>
                    <div className="flex items-center gap-6 text-xs font-semibold text-slate-600">
                        <div className="flex items-center gap-2.5"><span className="w-3.5 h-3.5 rounded-full bg-emerald-500 border border-emerald-600 shadow-sm"></span> Achats</div>
                        <div className="flex items-center gap-2.5"><span className="w-3.5 h-3.5 rounded-full bg-rose-500 border border-rose-600 shadow-sm"></span> Ventes</div>
                        <div className="flex items-center gap-2.5"><span className="w-3.5 h-3.5 rounded bg-purple-500 border border-purple-600 shadow-sm"></span> Dividendes</div>
                    </div>
                </div>
                <div className="flex-1 p-6 bg-white w-full h-full min-h-[400px] relative"><Chart options={chartOptions} series={series} type="area" height="100%" /></div>
            </div>
        </div>
    );
};

export const OperationsModal = ({ symbol, history, onClose }) => {
    const stockHistory = useMemo(() => {
        return history
            .filter(tx => tx.symbol === symbol)
            .sort((a, b) => new Date(b.date) - new Date(a.date));
    }, [history, symbol]);

    return (
        <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center z-[200] p-4 backdrop-blur-sm" onClick={onClose}>
            <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[85vh] flex flex-col animate-in zoom-in duration-200 shadow-2xl overflow-hidden border border-slate-100" onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center p-6 border-b border-slate-100 bg-slate-50 shrink-0">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-white border border-slate-200 rounded-2xl flex items-center justify-center text-indigo-600 shadow-sm">
                            <List size={24} />
                        </div>
                        <div>
                            <h3 className="font-black text-xl text-slate-900">Opérations sur {symbol}</h3>
                            <p className="text-sm font-bold text-slate-500">Historique complet des transactions</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="w-10 h-10 flex items-center justify-center rounded-full bg-white border border-slate-200 text-slate-400 hover:text-slate-900 hover:bg-slate-100 transition-colors shadow-sm">
                        <X size={20} />
                    </button>
                </div>
                <div className="overflow-y-auto p-0 flex-1">
                    {stockHistory.length > 0 ? (
                        <table className="w-full text-left text-sm">
                            <thead className="bg-white border-b border-slate-200 text-xs uppercase text-slate-500 font-bold sticky top-0 z-10 shadow-sm">
                                <tr>
                                    <th className="px-6 py-4">Date</th>
                                    <th className="px-6 py-4">Type</th>
                                    <th className="px-6 py-4 text-right">Quantité</th>
                                    <th className="px-6 py-4 text-right">Prix Unitaire</th>
                                    <th className="px-6 py-4 text-right">Frais</th>
                                    <th className="px-6 py-4 text-right">Montant Total</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {stockHistory.map(tx => {
                                    const isBuy = tx.type === 'BUY';
                                    const isSell = tx.type === 'SELL';
                                    const isDiv = tx.type === 'DIVIDEND';
                                    const total = isDiv ? tx.price : (tx.quantity * tx.price);
                                    return (
                                        <tr key={tx.id} className="hover:bg-slate-50 transition-colors">
                                            <td className="px-6 py-4 font-mono text-slate-600">{new Date(tx.date).toLocaleDateString()}</td>
                                            <td className="px-6 py-4">
                                                <span className={`inline-block px-2.5 py-1 rounded-md text-[10px] font-black uppercase tracking-wider ${isBuy ? 'bg-emerald-100 text-emerald-700' : isSell ? 'bg-rose-100 text-rose-700' : isDiv ? 'bg-purple-100 text-purple-700' : 'bg-slate-100 text-slate-600'}`}>
                                                    {tx.type}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-right font-mono font-medium text-slate-700">{tx.quantity > 0 && !isDiv ? formatQty(tx.quantity) : '-'}</td>
                                            <td className="px-6 py-4 text-right font-mono font-medium text-slate-700">{formatMoney(tx.price, tx.currency)}</td>
                                            <td className="px-6 py-4 text-right font-mono text-slate-400 text-xs">{tx.commission > 0 ? formatMoney(tx.commission, tx.currency) : '-'}</td>
                                            <td className="px-6 py-4 text-right font-mono font-bold text-slate-900">{formatMoney(total, tx.currency)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    ) : (
                        <div className="p-16 flex flex-col items-center justify-center text-slate-400">
                            <List size={48} className="mb-4 opacity-20" />
                            <p className="font-bold">Aucune opération trouvée pour cette action.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export const DividendGrowthChart = ({ data }) => {
    const { t } = useTranslation();
    if (!data || data.length < 2) return <div className="p-12 text-center text-slate-400 bg-white rounded-2xl border border-slate-200 shadow-sm mt-4">{t('portfolio.no_data', 'Aucune donnée.')}</div>;
    
    const totalAllTime = data.reduce((sum, item) => sum + item.amount, 0);

    return (
        <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-sm flex flex-col mt-4 animate-in fade-in duration-500 h-[500px]">
            <div className="flex items-center justify-between mb-6 border-b border-slate-100 pb-4">
                <h3 className="font-bold text-slate-800 flex items-center gap-2 text-sm uppercase tracking-wide">
                    <BarChart3 size={18} className="text-indigo-500"/> {t('portfolio.div_growth', 'Croissance des dividendes')}
                </h3>
                <div className="text-right">
                    <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">Total Historique</div>
                    <div className="text-lg font-black text-indigo-600">{formatMoney(totalAllTime, 'USD')}</div>
                </div>
            </div>
            
            <div className="flex-1 w-full">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                        <defs>
                            <linearGradient id="colorDiv" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.9}/>
                                <stop offset="95%" stopColor="#4f46e5" stopOpacity={0.9}/>
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                        <XAxis dataKey="year" tick={{fontSize: 11, fill: '#64748b', fontWeight: 'bold'}} axisLine={false} tickLine={false} />
                        <YAxis tickFormatter={(val) => `$${val}`} tick={{fontSize: 10, fill: '#94a3b8', fontWeight: 'bold'}} axisLine={false} tickLine={false} />
                        <RechartsTooltip 
                            cursor={{fill: '#f8fafc'}} 
                            contentStyle={{borderRadius:'12px', border:'none', boxShadow:'0 10px 25px -5px rgba(0,0,0,0.1)', fontWeight: 'bold'}} 
                            formatter={(val) => formatMoney(val, 'USD')} 
                        />
                        <Bar dataKey="amount" fill="url(#colorDiv)" radius={[6, 6, 0, 0]} maxBarSize={60}>
                            <LabelList dataKey="amount" position="top" formatter={(val) => `$${Math.round(val)}`} style={{fontSize: '10px', fill: '#64748b', fontWeight: 'bold'}} />
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};

export const DividendCalendar = ({ dividends, positions, forecast, growthData }) => {
    const { t } = useTranslation();
    const [view, setView] = useState('RECEIVED');
    const MONTHS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];

    const renderCalendarGrid = (dataList, isForecast = false) => {
        if (!dataList?.length) return <div className="text-center p-12 text-slate-400 bg-white rounded-2xl border border-slate-200 shadow-sm mt-4">{t('portfolio.no_data', 'Aucune donnée.')}</div>;
        
        const groupedByYear = dataList.reduce((acc, div) => { 
            const d = new Date(div.date); 
            const y = d.getFullYear(); 
            const m = d.getMonth(); 
            if(!acc[y]) acc[y] = Array(12).fill().map(() => []); 
            acc[y][m].push(div); 
            return acc; 
        }, {});
        
        const years = Object.keys(groupedByYear).sort((a,b) => isForecast ? a-b : b-a);

        return (
            <div className="space-y-12 mt-6 animate-in fade-in duration-500">
                {years.map(year => {
                    const monthDivsFlat = groupedByYear[year].flat();
                    const yearTotal = monthDivsFlat.reduce((acc, curr) => acc + (curr.amount * (curr.currency==='EUR'?1.05:1)), 0);
                    
                    return (
                        <div key={year} className="bg-slate-50/50 p-6 rounded-3xl border border-slate-100">
                            <div className="flex items-center justify-between mb-6 border-b border-slate-200 pb-4">
                                <h4 className="font-black text-2xl text-slate-900 flex items-center gap-3">
                                    <Calendar className={isForecast ? "text-blue-500" : "text-emerald-600"} size={28} /> 
                                    {year}
                                    <span className={`text-sm ml-2 px-3 py-1 rounded-full font-bold shadow-sm ${isForecast ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700'}`}>
                                        Total: {formatMoney(yearTotal, 'USD')}
                                    </span>
                                </h4>
                                {isForecast && <span className="text-xs font-black uppercase tracking-widest bg-white border border-slate-200 text-slate-400 px-3 py-1.5 rounded-lg shadow-sm">Prévisionnel</span>}
                            </div>
                            
                            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-5">
                                {groupedByYear[year].map((monthDivs, monthIndex) => {
                                    const monthTotal = monthDivs.reduce((acc, curr) => acc + (curr.amount * (curr.currency==='EUR'?1.05:1)), 0);
                                    
                                    return (
                                        <div key={monthIndex} className={`rounded-2xl border flex flex-col h-full overflow-hidden transition-shadow ${monthDivs.length > 0 ? 'bg-white shadow-sm hover:shadow-md border-slate-200' : 'bg-slate-50/50 border-slate-100 opacity-60'}`}>
                                            <div className={`px-4 py-2 border-b text-xs uppercase font-black tracking-wider ${isForecast ? 'bg-blue-50/50 border-blue-100 text-blue-700' : 'bg-emerald-50/50 border-emerald-100 text-emerald-700'}`}>
                                                {MONTHS[monthIndex]}
                                            </div>
                                            
                                            <div className="flex-1 p-3 space-y-2">
                                                {monthDivs.length > 0 ? ( 
                                                    monthDivs.map((d, i) => ( 
                                                        <div key={i} className="flex justify-between items-center text-xs group">
                                                            <span className="font-bold text-slate-600 group-hover:text-slate-900 transition-colors">{d.symbol}</span>
                                                            <span className={`font-mono font-bold ${isForecast ? 'text-blue-500' : 'text-emerald-600'}`}>+{d.amount.toFixed(2)}</span>
                                                        </div> 
                                                    )) 
                                                ) : ( 
                                                    <div className="h-full flex items-center justify-center text-slate-200 text-xs">-</div> 
                                                )}
                                            </div>
                                            
                                            {monthDivs.length > 0 && ( 
                                                <div className={`px-4 py-3 mt-auto border-t text-right ${isForecast ? 'bg-blue-50 border-blue-100' : 'bg-emerald-50 border-emerald-100'}`}>
                                                    <span className={`text-sm font-black ${isForecast ? 'text-blue-700' : 'text-emerald-700'}`}>
                                                        {formatMoney(monthTotal, 'USD')}
                                                    </span>
                                                </div> 
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <div>
            <div className="flex bg-slate-100 p-1.5 rounded-xl w-fit mb-4 shadow-inner border border-slate-200 overflow-x-auto no-scrollbar max-w-full">
                <button 
                    onClick={() => setView('RECEIVED')} 
                    className={`px-6 py-2.5 rounded-lg text-sm font-bold transition-all flex items-center gap-2 whitespace-nowrap ${view === 'RECEIVED' ? 'bg-white text-emerald-600 shadow-sm border border-slate-200/50' : 'text-slate-500 hover:text-slate-700'}`}
                >
                    <CheckCircle size={16} /> Reçus (Historique)
                </button>
                <button 
                    onClick={() => setView('UPCOMING')} 
                    className={`px-6 py-2.5 rounded-lg text-sm font-bold transition-all flex items-center gap-2 whitespace-nowrap ${view === 'UPCOMING' ? 'bg-white text-blue-600 shadow-sm border border-slate-200/50' : 'text-slate-500 hover:text-slate-700'}`}
                >
                    <Clock size={16} /> Prévisionnel (Estimations)
                </button>
                <button 
                    onClick={() => setView('GROWTH')} 
                    className={`px-6 py-2.5 rounded-lg text-sm font-bold transition-all flex items-center gap-2 whitespace-nowrap ${view === 'GROWTH' ? 'bg-white text-indigo-600 shadow-sm border border-slate-200/50' : 'text-slate-500 hover:text-slate-700'}`}
                >
                    <BarChart3 size={16} /> Croissance (Historique)
                </button>
            </div>
            {view === 'RECEIVED' && renderCalendarGrid(dividends, false)}
            {view === 'UPCOMING' && renderCalendarGrid(forecast, true)}
            {view === 'GROWTH' && <DividendGrowthChart data={growthData} />}
        </div>
    );
};

export const PerformanceChart = ({ data, benchmark, onBenchmarkChange }) => {
    const { t } = useTranslation();
    if (!data || data.length < 2) return <div className="flex flex-col items-center justify-center h-80 bg-white rounded-2xl border border-dashed border-slate-200 text-slate-400"><LineIcon size={48} className="mb-4 opacity-50"/><p>{t('portfolio.no_data')}</p></div>;
    
    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            const dateStr = new Date(label).toLocaleDateString();
            return (
                <div className="bg-white p-4 border border-slate-100 rounded-xl shadow-xl">
                    <p className="text-xs font-bold text-slate-500 mb-2 border-b border-slate-100 pb-2">{dateStr}</p>
                    {payload.map((entry, index) => (
                        <div key={index} className="flex items-center justify-between gap-4 mt-1">
                            <span className="text-sm font-semibold" style={{ color: entry.color }}>
                                {entry.name === 'performance' ? t('portfolio.my_portfolio', 'Portefeuille') : 'Benchmark'}
                            </span>
                            <span className="text-sm font-black font-mono text-slate-800">
                                {entry.value > 0 ? '+' : ''}{entry.value.toFixed(2)}%
                            </span>
                        </div>
                    ))}
                </div>
            );
        }
        return null;
    };

    return (
        <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-sm h-[500px] w-full">
            <div className="flex justify-between items-center mb-6">
                <h3 className="font-bold text-slate-800 flex items-center gap-2"><LineIcon size={18} className="text-indigo-600"/> {t('portfolio.perf_bench')}</h3>
                <div className="flex items-center gap-4">
                    <select value={benchmark} onChange={(e) => onBenchmarkChange(e.target.value)} className="text-xs bg-slate-50 border border-slate-200 rounded px-2 py-1 font-bold outline-none focus:border-indigo-500 cursor-pointer">
                        <option value="^GSPC">S&P 500</option>
                        <option value="^IXIC">NASDAQ</option>
                        <option value="URTH">MSCI World</option>
                        <option value="^FCHI">CAC 40</option>
                    </select>
                </div>
            </div>
            <div className="h-full w-full pb-8">
                <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={data} margin={{ top: 10, right: 0, left: -20, bottom: 0 }}>
                        <defs><linearGradient id="colorPerf" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#4f46e5" stopOpacity={0.1}/><stop offset="95%" stopColor="#4f46e5" stopOpacity={0}/></linearGradient></defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                        <XAxis dataKey="date" tickFormatter={(str) => { const d = new Date(str); return `${d.getDate()}/${d.getMonth()+1}`; }} tick={{fontSize: 10, fill: '#94a3b8'}} tickLine={false} axisLine={false} />
                        <YAxis domain={['auto', 'auto']} tickFormatter={(val) => `${val}%`} tick={{fontSize: 10, fill: '#94a3b8'}} tickLine={false} axisLine={false} />
                        <RechartsTooltip content={<CustomTooltip />} cursor={{ stroke: '#cbd5e1', strokeWidth: 1, strokeDasharray: '3 3' }} />
                        <Legend verticalAlign="top" height={36} iconType="circle" formatter={(value) => <span className="text-xs font-bold text-slate-600">{value === 'performance' ? t('portfolio.my_portfolio', 'Portefeuille') : 'Benchmark'}</span>}/>
                        <Area type="monotone" dataKey="performance" name="performance" stroke="#4f46e5" strokeWidth={2} fillOpacity={1} fill="url(#colorPerf)" />
                        <Line type="monotone" dataKey="benchmark" name="benchmark" stroke="#ef4444" strokeWidth={3} dot={false} connectNulls={true} />
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};

export const TaxonomyCharts = ({ data, positions }) => {
    const { t } = useTranslation();
    const positionData = useMemo(() => { if (!positions) return []; return positions.map(p => ({ name: p.symbol, value: p.value_converted || p.val_usd })); }, [positions]);
    const lynchData = useMemo(() => { if (!positions) return []; const res = {}; positions.forEach(p => { const cat = p.lynch_category || "Inconnu"; res[cat] = (res[cat] || 0) + (p.value_converted || p.val_usd); }); return Object.entries(res).map(([name, value]) => ({name, value})); }, [positions]);
    
    if (!data && !positions) return <div className="p-12 text-center text-slate-400">{t('portfolio.no_data')}</div>;

    const processData = (items) => { 
        if (!items || items.length === 0) return []; 
        const totalValue = items.reduce((acc, curr) => acc + curr.value, 0); 
        const sorted = [...items].sort((a, b) => b.value - a.value); 
        return sorted.map(i => ({...i, value: (i.value / totalValue) * 100})); 
    };

    const ChartCard = ({ title, rawData, color, bgLight, icon: Icon }) => {
        const cleanData = processData(rawData); 
        if (cleanData.length === 0) return null;
        
        return (
            <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm flex flex-col h-[400px]"> 
                <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-100 shrink-0">
                    <div className={`p-2 rounded-xl ${bgLight}`} style={{ color: color }}>
                        <Icon size={18}/>
                    </div>
                    <h3 className="font-black text-slate-800 text-sm uppercase tracking-wide">{title}</h3>
                </div>
                
                <div className="flex-1 overflow-y-auto pr-2 space-y-4 no-scrollbar">
                    {cleanData.map((item, idx) => (
                        <div key={`${item.name}-${idx}`} className="group">
                            <div className="flex justify-between items-center mb-1.5">
                                <span className="text-xs font-bold text-slate-600 truncate pr-2 group-hover:text-slate-900 transition-colors">{item.name}</span>
                                <span className="text-xs font-black font-mono" style={{ color: color }}>{item.value.toFixed(1)}%</span>
                            </div>
                            <div className="w-full bg-slate-100/80 rounded-full h-2 overflow-hidden shadow-inner">
                                <div 
                                    className="h-full rounded-full transition-all duration-1000 ease-out" 
                                    style={{ width: `${item.value}%`, backgroundColor: color }}
                                ></div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-6 items-start animate-in fade-in slide-in-from-bottom-4 duration-500">
            <ChartCard title={t('portfolio.cat_lynch', 'Lynch')} rawData={lynchData} color="#8b5cf6" bgLight="bg-purple-50" icon={Award} />
            <ChartCard title={t('portfolio.allocation', 'Allocation')} rawData={positionData} color="#3b82f6" bgLight="bg-blue-50" icon={Target} />
            <ChartCard title={t('portfolio.sectors', 'Secteurs')} rawData={data?.sectors} color="#6366f1" bgLight="bg-indigo-50" icon={PieIcon} />
            <ChartCard title={t('portfolio.countries', 'Pays')} rawData={data?.countries} color="#10b981" bgLight="bg-emerald-50" icon={PieIcon} />
            <ChartCard title={t('portfolio.industries', 'Industries')} rawData={data?.industries} color="#f59e0b" bgLight="bg-amber-50" icon={BarChart3} />
        </div>
    );
};

export const HistoryTable = ({ history, onDelete, onEdit }) => {
    const { t } = useTranslation();
    const [filters, setFilters] = useState({ symbol: '', type: 'ALL', date: '' });
    if (!history?.length) return <div className="text-center p-12 text-slate-400">{t('portfolio.history_empty')}</div>;
    const filteredHistory = history.filter(tx => tx.symbol.includes(filters.symbol.toUpperCase()) && (filters.type === 'ALL' || tx.type === filters.type) && (!filters.date || tx.date.startsWith(filters.date)));
    
    return (
        <div className="flex flex-col bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="flex flex-wrap gap-3 p-4 bg-slate-50 border-b border-slate-100 items-center">
                <Filter size={14} className="text-slate-400"/><input type="text" placeholder={t('portfolio.filter_symbol')} className="input-tiny w-24" value={filters.symbol} onChange={e=>setFilters({...filters, symbol: e.target.value})} />
                <select className="input-tiny" value={filters.type} onChange={e=>setFilters({...filters, type: e.target.value})}><option value="ALL">{t('portfolio.type_all')}</option><option value="BUY">{t('portfolio.type_buy')}</option><option value="SELL">{t('portfolio.type_sell')}</option><option value="DIVIDEND">{t('portfolio.type_div')}</option><option value="DEPOSIT">{t('portfolio.type_dep')}</option><option value="WITHDRAW">{t('portfolio.type_with')}</option><option value="TAX">{t('portfolio.type_tax')}</option></select>
                <input type="date" className="input-tiny" value={filters.date} onChange={e=>setFilters({...filters, date: e.target.value})} /><span className="ml-auto text-xs font-bold text-slate-400">{filteredHistory.length} ops</span>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                    <thead className="bg-slate-50 text-slate-500 uppercase font-bold text-xs border-b border-slate-200"><tr><th className="px-6 py-3">{t('portfolio.col_date')}</th><th className="px-6 py-3">{t('portfolio.col_type')}</th><th className="px-6 py-3">Symbole</th><th className="px-6 py-3 text-right">{t('portfolio.col_qty')}</th><th className="px-6 py-3 text-right">Prix</th><th className="px-6 py-3 text-center">{t('portfolio.col_note')}</th><th className="px-6 py-3 text-center">{t('portfolio.col_actions')}</th></tr></thead>
                    <tbody className="divide-y divide-slate-100">
                        {filteredHistory.map((tx) => (
                            <tr key={tx.id} className="hover:bg-slate-50 transition-colors">
                                <td className="px-6 py-3 font-mono text-slate-500 text-xs">{new Date(tx.date).toLocaleDateString()} <span className="text-slate-300 ml-1">{new Date(tx.date).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span></td>
                                <td className="px-6 py-3"><span className={`badge ${tx.type==='BUY'?'bg-indigo-100 text-indigo-700':tx.type==='SELL'?'bg-orange-100 text-orange-700':tx.type==='DIVIDEND'?'bg-emerald-100 text-emerald-700':'bg-slate-100 text-slate-600'}`}>{tx.type}</span></td>
                                <td className="px-6 py-3 font-bold text-slate-900">{tx.symbol}</td>
                                <td className="px-6 py-3 text-right font-mono">{tx.quantity > 0 ? formatQty(tx.quantity) : '-'}</td>
                                <td className="px-6 py-3 text-right font-mono text-slate-900">{formatMoney(tx.price, tx.currency)}</td>
                                <td className="px-6 py-3 text-center">{tx.notes && (<div className="relative group cursor-help inline-block"><BookOpen size={16} className="text-indigo-400 hover:text-indigo-600" onClick={() => alert(`Journal:\n${tx.notes}`)} /></div>)}</td>
                                <td className="px-6 py-3 text-center flex items-center justify-center gap-2">{onEdit && <button onClick={() => onEdit(tx)} className="text-slate-300 hover:text-indigo-600 transition-colors"><Edit size={14}/></button>}<button onClick={() => onDelete(tx.id)} className="text-slate-300 hover:text-red-500 transition-colors"><Trash2 size={14}/></button></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export const TechnicalRadar = ({ data }) => {
    const { t } = useTranslation();
    if (!data) return <div className="p-12 text-center text-slate-400 font-bold animate-pulse">Scan des marchés en cours...</div>;
    if (data.length === 0) return <div className="p-12 text-center text-slate-400">Aucune donnée technique disponible.</div>;

    const formatDist = (val) => {
        if (val === null || val === undefined || typeof val !== "number" || isNaN(val)) return "-";
        const isPos = val >= 0;
        return (
            <span className={`inline-flex items-center gap-1 font-mono text-xs font-bold px-2 py-1 rounded ${isPos ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-600"}`}>
                {isPos ? "+" : ""}{val.toFixed(2)}%
            </span>
        );
    };

    const getVerdictBadge = (verdict) => {
        switch(verdict) {
            case "Survente": return "bg-emerald-100 text-emerald-700 border-emerald-200";
            case "Surachat": return "bg-rose-100 text-rose-700 border-rose-200";
            case "Haussier": return "bg-indigo-100 text-indigo-700 border-indigo-200";
            case "Baissier": return "bg-orange-100 text-orange-700 border-orange-200";
            default: return "bg-slate-100 text-slate-600 border-slate-200";
        }
    };

    return (
        <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden mb-6">
            <div className="px-6 py-5 border-b border-slate-100 bg-slate-900 text-white flex items-center justify-between">
                <div>
                    <h3 className="font-black text-lg flex items-center gap-2"><Activity size={20} className="text-indigo-400"/> {t("portfolio.radar_title") || "Radar Technique & Tendance"}</h3>
                    <p className="text-xs text-slate-400 mt-1">{t("portfolio.radar_desc") || "Vue tactique court et moyen terme."}</p>
                </div>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase text-slate-500 font-bold">
                        <tr>
                            <th className="px-6 py-4">Action</th>
                            <th className="px-6 py-4 text-center">RSI (14)</th>
                            <th className="px-6 py-4 text-center">{t("portfolio.radar_col_trend50") || "Tendance (SMA 50)"}</th>
                            <th className="px-6 py-4 text-center">{t("portfolio.radar_col_trend200") || "Fond (SMA 200)"}</th>
                            <th className="px-6 py-4 text-center">{t("portfolio.radar_col_verdict") || "Verdict Tactique"}</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {data.map(item => (
                            <tr key={item.symbol} className="hover:bg-slate-50 transition-colors">
                                <td className="px-6 py-4">
                                    <div className="font-bold text-slate-900">{item.symbol}</div>
                                    <div className="text-xs text-slate-400">{item.price && !isNaN(item.price) ? `${Number(item.price).toFixed(2)} $` : "-"}</div>
                                </td>
                                <td className="px-6 py-4 text-center">
                                    {item.rsi && !isNaN(item.rsi) ? (
                                        <span className={`inline-block px-2 py-1 rounded text-xs font-black ${item.rsi < 30 ? "bg-emerald-100 text-emerald-700" : item.rsi > 70 ? "bg-rose-100 text-rose-700" : "bg-slate-100 text-slate-600"}`}>
                                            {Number(item.rsi).toFixed(0)}
                                        </span>
                                    ) : <span className="text-slate-300">-</span>}
                                </td>
                                <td className="px-6 py-4 text-center">
                                    <div className="flex flex-col items-center justify-center gap-1">
                                        <span className="text-xs text-slate-400 font-mono">{item.sma50 && !isNaN(item.sma50) ? Number(item.sma50).toFixed(2) : "-"}</span>
                                        {formatDist(item.dist_50)}
                                    </div>
                                </td>
                                <td className="px-6 py-4 text-center">
                                    <div className="flex flex-col items-center justify-center gap-1">
                                        <span className="text-xs text-slate-400 font-mono">{item.sma200 && !isNaN(item.sma200) ? Number(item.sma200).toFixed(2) : "-"}</span>
                                        {formatDist(item.dist_200)}
                                    </div>
                                </td>
                                <td className="px-6 py-4 text-center">
                                    <span className={`px-3 py-1.5 rounded-lg text-xs font-black border uppercase tracking-wider ${getVerdictBadge(item.verdict)}`}>
                                        {item.verdict || "-"}
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export const SectorPerformanceHistory = ({ portfolioId }) => {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
    const [viewMode, setViewMode] = useState('MONTHLY');

    useEffect(() => {
        if (!portfolioId) return;
        setLoading(true);
        api.get(`/portfolio/${portfolioId}/performance-matrix`)
           .then(res => setData(res.data.sectors || {}))
           .catch(e => console.error(e))
           .finally(() => setLoading(false));
    }, [portfolioId]);

    if (loading) return <div className="p-12 flex justify-center"><RefreshCcw className="animate-spin text-indigo-500" size={32}/></div>;
    if (!data || Object.keys(data).length === 0) return <div className="p-12 text-center text-slate-400 font-bold bg-white rounded-3xl border border-slate-200">Aucune donnée sectorielle disponible pour créer l'historique.</div>;

    const availableYears = [...new Set(Object.values(data).flatMap(sec => Object.keys(sec)))].sort().reverse();
    if (!availableYears.includes(selectedYear) && availableYears.length > 0) setSelectedYear(availableYears[0]);

    const sectors = Object.keys(data);
    const COLORS = ['#4f46e5', '#10b981', '#f59e0b', '#ec4899', '#8b5cf6', '#06b6d4', '#84cc16', '#ef4444', '#64748b', '#0ea5e9', '#f43f5e', '#14b8a6'];

    const calculateQuarterlyData = (yearData) => {
        const quarters = { 'Q1': [1,2,3], 'Q2': [4,5,6], 'Q3': [7,8,9], 'Q4': [10,11,12] };
        let qData = {};
        for (const [qName, months] of Object.entries(quarters)) {
            let composedReturn = 1.0;
            let hasData = false;
            months.forEach(m => {
                const val = yearData[m.toString()];
                if (val !== undefined && val !== 0) {
                    composedReturn *= (1 + (val / 100));
                    hasData = true;
                }
            });
            qData[qName] = hasData ? (composedReturn - 1) * 100 : undefined;
        }
        qData['TOTAL'] = yearData['TOTAL'];
        return qData;
    };

    const periods = viewMode === 'MONTHLY' 
        ? ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12']
        : ['Q1', 'Q2', 'Q3', 'Q4'];
    
    const periodLabels = viewMode === 'MONTHLY'
        ? ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc']
        : ['T1 (Jan-Mar)', 'T2 (Avr-Juin)', 'T3 (Juil-Sep)', 'T4 (Oct-Déc)'];

    const chartData = periods.map((p, idx) => {
        let obj = { name: periodLabels[idx] };
        sectors.forEach(sec => {
            const rawYearData = data[sec]?.[selectedYear] || {};
            const pData = viewMode === 'MONTHLY' ? rawYearData : calculateQuarterlyData(rawYearData);
            obj[sec] = pData[p] || 0;
        });
        return obj;
    });

    return (
        <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 overflow-hidden">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6 border-b border-slate-100 pb-4">
                <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-indigo-50 text-indigo-600 rounded-xl shadow-inner border border-indigo-100">
                        <PieIcon size={20}/>
                    </div>
                    <h3 className="font-black text-slate-800 text-lg tracking-tight">Rendements Sectoriels</h3>
                </div>
                
                <div className="flex items-center gap-4">
                    <div className="flex bg-slate-100 p-1 rounded-lg">
                        <button onClick={() => setViewMode('MONTHLY')} className={`px-4 py-1.5 rounded-md text-xs font-bold transition-all ${viewMode === 'MONTHLY' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>Mensuel</button>
                        <button onClick={() => setViewMode('QUARTERLY')} className={`px-4 py-1.5 rounded-md text-xs font-bold transition-all ${viewMode === 'QUARTERLY' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>Trimestriel</button>
                    </div>
                    
                    <select value={selectedYear} onChange={e => setSelectedYear(e.target.value)} className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-2 font-black text-slate-700 outline-none cursor-pointer focus:border-indigo-500 shadow-sm">
                        {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                </div>
            </div>

            <div className="h-80 w-full mb-8">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                        <XAxis dataKey="name" tick={{fontSize: 10, fill: '#64748b', fontWeight: 'bold'}} axisLine={false} tickLine={false} />
                        <YAxis tickFormatter={(val) => `${val}%`} tick={{fontSize: 10, fill: '#64748b', fontWeight: 'bold'}} axisLine={false} tickLine={false} />
                        <RechartsTooltip cursor={{fill: '#f8fafc'}} contentStyle={{borderRadius:'16px', border:'none', boxShadow:'0 10px 25px -5px rgba(0,0,0,0.1)'}} formatter={(val) => `${val.toFixed(2)}%`} />
                        <Legend wrapperStyle={{fontSize: '11px', fontWeight: 'bold', paddingTop: '10px'}} />
                        {sectors.map((sec, idx) => (
                            <Bar key={sec} dataKey={sec} stackId="a" fill={COLORS[idx % COLORS.length]} radius={[2, 2, 2, 2]} />
                        ))}
                    </BarChart>
                </ResponsiveContainer>
            </div>

            <div className="overflow-x-auto border border-slate-100 rounded-2xl">
                <table className="w-full text-center text-sm">
                    <thead className="bg-slate-50 border-b border-slate-100">
                        <tr>
                            <th className="px-4 py-3 text-left text-xs font-black text-slate-500 uppercase tracking-wider">Secteur</th>
                            {periodLabels.map((m, i) => <th key={i} className="px-3 py-3 text-xs font-bold text-slate-400 uppercase tracking-wider">{viewMode === 'QUARTERLY' ? periods[i] : m}</th>)}
                            <th className="px-4 py-3 text-xs font-black text-slate-800 uppercase tracking-wider border-l border-slate-100">YTD</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                        {sectors.map(sec => {
                            const rawYearData = data[sec]?.[selectedYear] || {};
                            const yData = viewMode === 'MONTHLY' ? rawYearData : calculateQuarterlyData(rawYearData);
                            
                            return (
                                <tr key={sec} className="hover:bg-slate-50/50 transition-colors">
                                    <td className="px-4 py-3 text-left font-bold text-slate-700 whitespace-nowrap text-xs">{sec}</td>
                                    {periods.map(p => {
                                        const val = yData[p];
                                        if (val === undefined || val === 0) return <td key={p} className="px-3 py-3 text-slate-300 font-mono">-</td>;
                                        return (
                                            <td key={p} className={`px-3 py-3 font-mono text-xs font-bold ${val > 0 ? 'text-emerald-700 bg-emerald-50/40' : val < 0 ? 'text-rose-700 bg-rose-50/40' : 'text-slate-400'}`}>
                                                {val > 0 ? '+' : ''}{val.toFixed(1)}%
                                            </td>
                                        );
                                    })}
                                    <td className={`px-4 py-3 font-mono text-sm font-black border-l border-slate-100 ${yData['TOTAL'] > 0 ? 'text-emerald-600 bg-emerald-50' : yData['TOTAL'] < 0 ? 'text-rose-600 bg-rose-50' : 'text-slate-400'}`}>
                                        {yData['TOTAL'] !== undefined ? `${yData['TOTAL'] > 0 ? '+' : ''}${yData['TOTAL'].toFixed(1)}%` : '-'}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export const VolatilityDashboard = ({ metrics }) => {
    const { t } = useTranslation();
    if (!metrics) return <div className="p-12 text-center text-slate-400 font-bold animate-pulse">Calcul des risques en cours...</div>;
    const { beta, sharpe, max_drawdown } = metrics;

    const MetricGauge = ({ title, value, min, max, optimal, unit = "", icon: Icon, desc }) => {
        const percent = Math.min(Math.max(((value - min) / (max - min)) * 100, 0), 100);
        let color = "bg-slate-300";
        if (title.includes("Beta")) color = value > 1.2 ? "bg-rose-500" : value < 0.8 ? "bg-blue-500" : "bg-emerald-500";
        if (title.includes("Sharpe")) color = value > 1 ? "bg-emerald-500" : value < 0.5 ? "bg-rose-500" : "bg-amber-500";
        if (title.includes("Drawdown")) color = Math.abs(value) > 30 ? "bg-rose-500" : Math.abs(value) < 15 ? "bg-emerald-500" : "bg-amber-500";

        return (
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex flex-col h-full">
                <div className="flex items-center gap-3 mb-4">
                    <div className={`p-2 rounded-xl text-white ${color.replace('bg-', '') === 'slate-300' ? 'bg-slate-800' : color}`}>
                        <Icon size={20} />
                    </div>
                    <h4 className="font-black text-slate-800 uppercase tracking-tight">{title}</h4>
                </div>
                
                <div className="flex items-end gap-2 mb-4">
                    <span className="text-4xl font-black text-slate-900">{value}{unit}</span>
                    <span className="text-sm font-bold text-slate-400 mb-1">{optimal}</span>
                </div>

                <div className="relative w-full h-3 bg-slate-100 rounded-full overflow-hidden mb-4">
                    <div className={`absolute top-0 left-0 h-full rounded-full transition-all duration-1000 ${color}`} style={{ width: `${percent}%` }}></div>
                </div>
                
                <p className="text-xs text-slate-500 font-medium leading-relaxed flex-grow">{desc}</p>
            </div>
        );
    };

    return (
        <div className="space-y-6">
            <div className="bg-slate-900 rounded-2xl p-6 text-white flex items-center justify-between shadow-lg">
                <div>
                    <h3 className="font-black text-xl flex items-center gap-2"><ShieldCheck size={24} className="text-indigo-400"/> Tableau de Bord des Risques</h3>
                    <p className="text-sm text-slate-400 mt-1">Analyse de la volatilité et des performances ajustées au risque par rapport au marché.</p>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <MetricGauge 
                    title="Beta (Volatilité)" 
                    value={beta} 
                    min={0} max={2} 
                    optimal="Idéal: ~1.0" 
                    icon={Activity}
                    desc={beta > 1.2 ? "Haute volatilité. Votre portefeuille amplifie les mouvements du marché." : beta < 0.8 ? "Faible volatilité. Votre portefeuille amortit les chocs du marché." : "Volatilité neutre, similaire au marché de référence."}
                />
                <MetricGauge 
                    title="Ratio de Sharpe" 
                    value={sharpe} 
                    min={0} max={3} 
                    optimal="Idéal: > 1.0" 
                    icon={Award}
                    desc={sharpe > 1 ? "Excellent. Le rendement obtenu justifie largement le risque pris." : sharpe < 0.5 ? "Médiocre. Le risque pris n'est pas suffisamment rémunéré." : "Correct. Performance standard par rapport au risque."}
                />
                <MetricGauge 
                    title="Max Drawdown" 
                    value={max_drawdown} 
                    min={-50} max={0} 
                    unit="%"
                    optimal="Idéal: > -15%" 
                    icon={AlertTriangle}
                    desc={"Représente la perte maximale historique subie par le portefeuille depuis son plus haut point."}
                />
            </div>
        </div>
    );
};

export const ModernStatsDashboard = ({ portfolioId, summary, analytics }) => {
    const { t } = useTranslation();
    const [advancedData, setAdvancedData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (portfolioId) {
            setLoading(true);
            api.get(`/portfolio/${portfolioId}/advanced-metrics`)
               .then(res => {
                   setAdvancedData(res.data.fundamentals);
               })
               .catch(e => console.error("Erreur metrics", e))
               .finally(() => setLoading(false));
        }
    }, [portfolioId]);

    if (!summary || !summary.positions) return <div className="p-12 text-center text-slate-400">Chargement des données...</div>;

    const positions = summary.positions.filter(p => p.symbol !== 'CASH');

    const totalAssets = positions.length;
    const sortedByWeight = [...positions].sort((a,b) => b.weight - a.weight);
    const top5Weight = sortedByWeight.slice(0, 5).reduce((acc, p) => acc + p.weight, 0);
    const flop5Weight = sortedByWeight.slice(-5).reduce((acc, p) => acc + p.weight, 0);
    
    const metrics = analytics?.metrics || {};
    const sharpe = metrics.sharpe || "-";
    const beta = metrics.beta || "-";
    const totalReturn = summary.total_pl || 0;
    const returnPct = summary.total_invested > 0 ? (totalReturn / summary.total_invested) * 100 : 0;

    const fund = advancedData || {};

    const StatSection = ({ title, icon: Icon, children }) => (
        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex flex-col h-full">
            <h3 className="font-black text-slate-800 mb-6 flex items-center gap-2 border-b border-slate-100 pb-3">
                <div className="p-2 bg-indigo-50 text-indigo-600 rounded-xl"><Icon size={18}/></div>
                {title}
            </h3>
            <div className="flex-1 space-y-4">
                {children}
            </div>
        </div>
    );

    const StatRow = ({ label, value, tooltip, highlight = false, colorClass = "text-slate-900" }) => (
        <div className="flex justify-between items-center text-sm py-1">
            <div className="flex items-center gap-1.5 group relative">
                <span className="font-bold text-slate-500">{label}</span>
                {tooltip && (
                    <div className="cursor-help text-slate-300 hover:text-indigo-400 transition-colors">
                        <Info size={14} />
                        <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block w-64 p-3 bg-slate-800 text-white text-xs rounded-xl shadow-xl z-50 pointer-events-none whitespace-pre-wrap leading-relaxed">
                            {tooltip}
                            <div className="absolute left-3 top-full w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-transparent border-t-slate-800"></div>
                        </div>
                    </div>
                )}
            </div>
            <span className={`font-black font-mono ${highlight ? 'text-base' : ''} ${colorClass} shrink-0 ml-2`}>
                {loading ? "..." : value}
            </span>
        </div>
    );

    return (
        <div className="space-y-6">
            <div className="bg-slate-900 rounded-2xl p-6 text-white flex items-center justify-between shadow-lg">
                <div>
                    <h3 className="font-black text-xl flex items-center gap-2"><BarChart3 size={24} className="text-indigo-400"/> Analyse Fondamentale & Quantitative</h3>
                    <p className="text-sm text-slate-400 mt-1">Vue d'ensemble des métriques moyennes pondérées de votre portefeuille consolidé.</p>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                
                <StatSection title="Général" icon={Layers}>
                    <StatRow label="Nombre d'actions" value={totalAssets} highlight={true} tooltip="Le nombre total de positions distinctes dans ce portefeuille (hors liquidités)." />
                    <StatRow label="Top 5 du portefeuille" value={`${top5Weight.toFixed(1)}%`} tooltip="Le pourcentage du portefeuille concentré sur vos 5 plus grandes positions." />
                    <StatRow label="Flop 5 du portefeuille" value={`${flop5Weight.toFixed(1)}%`} tooltip="Le pourcentage du portefeuille concentré sur vos 5 plus petites positions." />
                    <StatRow label="Market Cap moy." value={fund.market_cap_formatted || "--"} tooltip="La capitalisation boursière moyenne des entreprises de votre portefeuille." />
                </StatSection>

                <StatSection title="Indicateurs de Performance" icon={TrendingUp}>
                    <StatRow label="Taux de rendement global" value={`${returnPct > 0 ? '+' : ''}${returnPct.toFixed(2)}%`} colorClass={returnPct >= 0 ? "text-emerald-500" : "text-rose-500"} highlight={true} tooltip="La rentabilité historique absolue de vos investissements (Plus-values latentes et encaissées)." />
                    <StatRow label="Rendement par dividende" value={fund.div_yield !== undefined ? `${fund.div_yield}%` : "--"} colorClass="text-indigo-500" tooltip="Le pourcentage de revenu annuel généré sous forme de dividendes." />
                    <StatRow label="Ratio de Sharpe" value={fund.sharpe !== undefined ? fund.sharpe : sharpe} tooltip="Mesure le rendement obtenu par unité de risque (volatilité)." />
                    <StatRow label="Ratio de Sortino" value={fund.sortino !== undefined ? fund.sortino : "--"} tooltip="Similaire au Sharpe, mais ne pénalise que la volatilité négative." />
                    <StatRow label="Bêta (Volatilité)" value={fund.beta !== undefined ? fund.beta : beta} tooltip="La sensibilité de votre portefeuille par rapport au marché global." />
                    <StatRow label="Écart-type (Volatilité)" value={fund.volatility !== undefined ? `${fund.volatility}%` : "--"} tooltip="La variation moyenne annuelle de la valeur de votre portefeuille." />
                </StatSection>

                <StatSection title="Évaluation & Diversification" icon={PieIcon}>
                    <StatRow label="Alpha de Jensen" value={fund.alpha !== undefined ? `${fund.alpha > 0 ? '+' : ''}${fund.alpha}%` : "--"} colorClass={fund.alpha > 0 ? "text-emerald-500" : "text-slate-900"} tooltip="La surperformance générée par rapport au risque que vous avez pris." />
                    <StatRow label="Corrélation au Benchmark" value={fund.correlation !== undefined ? fund.correlation : "--"} tooltip="Le degré de similitude de vos mouvements avec le marché." />
                    <StatRow label="PE Ratio moyen" value={fund.pe_ratio !== undefined ? `${fund.pe_ratio}x` : "--"} tooltip="Price-to-Earnings Ratio. Le multiple des bénéfices payé pour vos actions." />
                </StatSection>

                <StatSection title="Marges Moyennes Pondérées" icon={Target}>
                    <StatRow label="Marge brute" value={fund.gross_margin !== undefined ? `${fund.gross_margin}%` : "--"} colorClass="text-emerald-600" tooltip="Le bénéfice restant après déduction du coût de fabrication des produits." />
                    <StatRow label="Marge opérationnelle" value={fund.operating_margin !== undefined ? `${fund.operating_margin}%` : "--"} colorClass="text-emerald-600" tooltip="Le bénéfice lié à l'exploitation normale de l'entreprise." />
                    <StatRow label="Marge nette" value={fund.net_margin !== undefined ? `${fund.net_margin}%` : "--"} colorClass="text-emerald-600" tooltip="Le bénéfice net final." />
                </StatSection>

                <StatSection title="Retours sur Capitaux" icon={Award}>
                    <StatRow label="ROIC" value={fund.roic !== undefined ? `${fund.roic}%` : "--"} colorClass="text-indigo-600" tooltip="Return on Invested Capital. La rentabilité de tous les capitaux investis." />
                    <StatRow label="ROCE" value={fund.roce !== undefined ? `${fund.roce}%` : "--"} colorClass="text-indigo-600" tooltip="Return on Capital Employed." />
                    <StatRow label="ROE" value={fund.roe !== undefined ? `${fund.roe}%` : "--"} colorClass="text-indigo-600" tooltip="Return on Equity. La rentabilité de l'argent des actionnaires." />
                </StatSection>

                <StatSection title="Croissance (Pondérée)" icon={Activity}>
                    <StatRow label="Croissance revenus" value={fund.rev_growth !== undefined ? `${fund.rev_growth}%` : "--"} colorClass="text-blue-600" tooltip="La croissance du chiffre d'affaires sur la dernière année écoulée." />
                    <StatRow label="Croissance BPA (EPS)" value={fund.eps_growth !== undefined ? `${fund.eps_growth}%` : "--"} colorClass="text-blue-600" tooltip="La croissance du Bénéfice Par Action." />
                    <StatRow label="Croissance FCF" value={fund.fcf_growth !== undefined ? `${fund.fcf_growth}%` : "--"} colorClass="text-blue-600" tooltip="La croissance du Free Cash Flow." />
                </StatSection>

            </div>
        </div>
    );
};

export const PortfolioHeatmap = ({ summary }) => {
    const { t } = useTranslation();

    const getColor = (gain) => {
        if (gain >= 20) return '#059669'; 
        if (gain >= 5) return '#10b981';  
        if (gain >= 0) return '#34d399';  
        if (gain > -5) return '#f87171';  
        if (gain > -20) return '#ef4444'; 
        return '#dc2626'; 
    };

    const series = useMemo(() => {
        if (!summary || !summary.positions) return [];
        const sectors = {};
        summary.positions.forEach(p => {
            if (p.symbol === 'CASH' || p.value_converted <= 0) return;
            const sec = p.sector || 'Autre';
            if (!sectors[sec]) sectors[sec] = [];
            sectors[sec].push({
                x: p.symbol,
                y: Math.round(p.value_converted),
                fillColor: getColor(p.gain_p),
                gain: p.gain_p
            });
        });

        return Object.keys(sectors).map(sec => ({
            name: sec,
            data: sectors[sec].sort((a, b) => b.y - a.y)
        }));
    }, [summary]);

    if (!summary || !summary.positions || summary.positions.length === 0) {
        return <div className="p-12 text-center text-slate-400">{t('portfolio.no_data', 'Aucune donnée.')}</div>;
    }

    const chartOptions = {
        chart: {
            type: 'treemap',
            fontFamily: 'system-ui, -apple-system, sans-serif',
            toolbar: { show: false },
            animations: { enabled: true, easing: 'easeinout', speed: 800 }
        },
        legend: { show: true, position: 'top', fontWeight: 'bold' },
        stroke: { show: true, width: 2, colors: ['#ffffff'] },
        dataLabels: {
            enabled: true,
            style: { fontSize: '14px', fontWeight: 'bold', colors: ['#ffffff'] },
            formatter: function(text, op) {
                const data = op.w.globals.initialSeries[op.seriesIndex].data[op.dataPointIndex];
                return [text, `${data.gain > 0 ? '+' : ''}${data.gain.toFixed(1)}%`];
            }
        },
        plotOptions: {
            treemap: {
                enableShades: false, 
                useFillColorAsStroke: false
            }
        },
        tooltip: {
            custom: function({series, seriesIndex, dataPointIndex, w}) {
                const data = w.globals.initialSeries[seriesIndex].data[dataPointIndex];
                const isPos = data.gain >= 0;
                const colorClass = isPos ? 'text-emerald-600' : 'text-rose-600';
                const sign = isPos ? '+' : '';
                return `
                    <div class="p-3 bg-white shadow-xl rounded-xl border border-slate-100 min-w-[150px]">
                        <div class="font-black text-slate-800 text-lg mb-1">${data.x}</div>
                        <div class="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">${w.globals.initialSeries[seriesIndex].name}</div>
                        <div class="flex justify-between items-center mb-1 gap-4">
                            <span class="text-xs text-slate-400">Poids :</span>
                            <span class="text-sm font-mono font-bold text-slate-700">$${data.y.toLocaleString('en-US')}</span>
                        </div>
                        <div class="flex justify-between items-center gap-4">
                            <span class="text-xs text-slate-400">PV Latente :</span>
                            <span class="text-sm font-bold ${colorClass}">${sign}${data.gain.toFixed(2)}%</span>
                        </div>
                    </div>
                `;
            }
        }
    };

    return (
        <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 overflow-hidden h-[600px] flex flex-col animate-in fade-in duration-500">
            <div className="flex justify-between items-start md:items-center mb-4 flex-col md:flex-row gap-4">
                <div>
                    <h3 className="font-black text-xl text-slate-900">Heatmap du Portefeuille</h3>
                    <p className="text-sm font-medium text-slate-500 mt-1">
                        Taille = Valeur de la position | Couleur = Plus-Value Latente (PRU)
                    </p>
                </div>
                <div className="flex items-center gap-2 text-[10px] font-bold uppercase text-slate-500 border border-slate-200 p-2 rounded-xl bg-slate-50 shadow-inner">
                    <div className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-rose-600 shadow-sm"></span> -20%+</div>
                    <div className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-rose-400 shadow-sm"></span> &lt;0%</div>
                    <div className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-400 shadow-sm"></span> &gt;0%</div>
                    <div className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-600 shadow-sm"></span> +20%+</div>
                </div>
            </div>
            <div className="flex-1 w-full h-full relative">
                <Chart options={chartOptions} series={series} type="treemap" height="100%" />
            </div>
        </div>
    );
};

export { default as PerformanceMatrix } from '../components/portfolio/PerformanceMatrix';
